export type MarketingConsentState = 'accepted' | 'rejected' | 'nao';

export const MARKETING_CONSENT_KEY = 'politic';
export const MARKETING_CONSENT_COOKIE_MAX_AGE_SEC = 60 * 60 * 24 * 365;

const VALID_MARKETING_CONSENTS: ReadonlySet<MarketingConsentState> = new Set<MarketingConsentState>([
  'accepted',
  'rejected',
  'nao',
]);

function parseCookieString(cookieSource: string | null | undefined): Record<string, string> {
  if (!cookieSource) return {};

  return cookieSource
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .reduce<Record<string, string>>((acc, part) => {
      const separatorIndex = part.indexOf('=');
      const rawName = separatorIndex >= 0 ? part.slice(0, separatorIndex) : part;
      const rawValue = separatorIndex >= 0 ? part.slice(separatorIndex + 1) : '';

      if (!rawName) return acc;

      acc[decodeURIComponent(rawName)] = decodeURIComponent(rawValue || '');
      return acc;
    }, {});
}

export function normalizeMarketingConsent(value: unknown): MarketingConsentState {
  const normalized = String(value || '').trim().toLowerCase() as MarketingConsentState;
  return VALID_MARKETING_CONSENTS.has(normalized) ? normalized : 'nao';
}

export function hasAcceptedMarketingConsent(value: unknown): boolean {
  return normalizeMarketingConsent(value) === 'accepted';
}

export function getMarketingConsentFromCookieString(
  cookieSource: string | null | undefined
): MarketingConsentState {
  return normalizeMarketingConsent(parseCookieString(cookieSource)[MARKETING_CONSENT_KEY]);
}

export function getMarketingConsentFromDocument(): MarketingConsentState {
  if (typeof document === 'undefined') return 'nao';
  return getMarketingConsentFromCookieString(document.cookie);
}

export function getMarketingConsentFromLocalStorage(): MarketingConsentState {
  if (typeof window === 'undefined') return 'nao';

  try {
    return normalizeMarketingConsent(window.localStorage.getItem(MARKETING_CONSENT_KEY));
  } catch {
    return 'nao';
  }
}

export function resolveMarketingConsentState(
  cookieConsent: MarketingConsentState,
  storageConsent: MarketingConsentState
): MarketingConsentState {
  if (cookieConsent !== 'nao') return cookieConsent;
  return storageConsent;
}

export function writeMarketingConsentCookie(
  value: MarketingConsentState | null | undefined
): void {
  if (typeof document === 'undefined') return;

  if (!value) {
    document.cookie = `${MARKETING_CONSENT_KEY}=; Max-Age=0; Path=/; SameSite=Lax`;
    return;
  }

  const normalized = normalizeMarketingConsent(value);
  document.cookie = `${MARKETING_CONSENT_KEY}=${encodeURIComponent(normalized)}; Max-Age=${MARKETING_CONSENT_COOKIE_MAX_AGE_SEC}; Path=/; SameSite=Lax`;
}

export function writeMarketingConsentLocalStorage(
  value: MarketingConsentState | null | undefined
): void {
  if (typeof window === 'undefined') return;

  try {
    if (!value) {
      window.localStorage.removeItem(MARKETING_CONSENT_KEY);
      return;
    }

    window.localStorage.setItem(MARKETING_CONSENT_KEY, normalizeMarketingConsent(value));
  } catch {
    // Ignore storage failures to avoid breaking navigation.
  }
}
